import type { ExecutePayloadInput } from '../models/execute.types'
import {
  FIXED_OUTCOME_ORDER,
  type InsightResult,
  type OutcomeCategory,
} from '../models/insight.types'
import type { InsightExecutionForm } from '../models/workbench.types'

import {
  outcomePresentation,
  summarizeSeverityBreakdown,
  type OutcomePresentation,
  type SeverityBreakdownItem,
} from './insightPresentation'

export interface InsightSummaryCardModel extends OutcomePresentation {
  outcome: OutcomeCategory
  count: number
  progress: number
  totalImpact: number
  severity: SeverityBreakdownItem[]
}

export function createInsightExecutionForm(): InsightExecutionForm {
  return {
    config_id: null,
    config_enum: '',
    schedule_id: '',
    force: false,
    notify: false,
  }
}

function numericId(value: unknown): number | null {
  if (value === undefined || value === null || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

/** The measured legacy dry-run signature. Notify and dry_run deliberately do not participate. */
export function buildPreviewRequestSignature(payload: ExecutePayloadInput): string {
  return JSON.stringify({
    config_id: numericId(payload.config_id),
    config_enum: typeof payload.config_enum === 'string' ? payload.config_enum.trim() : '',
    schedule_id: numericId(payload.schedule_id),
    force: Boolean(payload.force),
  })
}

export function hasExecutionConfig(payload: ExecutePayloadInput): boolean {
  return (
    numericId(payload.config_id) !== null ||
    (typeof payload.config_enum === 'string' && payload.config_enum.trim().length > 0)
  )
}

export function toExecutePayload(form: InsightExecutionForm, dryRun: boolean): ExecutePayloadInput {
  return {
    config_id: form.config_id,
    config_enum: form.config_enum,
    schedule_id: form.schedule_id,
    force: form.force,
    dry_run: dryRun,
    notify: form.notify,
  }
}

/** The workbench represents the backend's all-platform sentinel as an omitted server filter. */
export function normalizeWorkbenchPlatformFilter(value: string): string {
  const normalized = value.trim()
  return normalized === 'all' ? '' : normalized
}

export function summarizeWorkbenchOutcomes(
  insights: readonly InsightResult[],
): InsightSummaryCardModel[] {
  const total = insights.length
  return FIXED_OUTCOME_ORDER.map((outcome) => {
    const items = insights.filter((item) => item.outcome_category === outcome)
    return {
      ...outcomePresentation(outcome),
      outcome,
      count: items.length,
      progress: total === 0 ? 0 : (items.length / total) * 100,
      totalImpact: items.reduce((sum, item) => sum + Number(item.impact_score ?? 0), 0),
      severity: summarizeSeverityBreakdown(items),
    }
  })
}
